import { computed, observable } from 'mobx'
import {
  getRynoTypeFromJsType,
  RynoSymbol,
  SymbolKind,
  SymbolTable,
  unify,
  Variance,
} from '@taxfyle/ryno'
import type {
  EngineSymbols,
  OrganizerBindings,
} from '@/features/legends/legend-engine'
import type {
  Entity,
  EntityDescriptor,
  EntityProvider,
  SymbolContext,
} from '@/features/legends/editor/entities'
import { generateIdFromText } from '@/features/legends/editor/entity-ids'
import type { SymbolScope } from '@/features/legends/editor/legend-part-dependencies'
import type { RynoType } from '@/features/legends/editor/ryno-types'

/**
 * The legend's symbols: the engine's, and the entities its builders declare,
 * in the builders' order. Each builder sees the symbols declared by the
 * builders before it.
 */
export class LegendSymbolScope implements SymbolScope {
  /**
   * IDs generated for entities that don't have one yet, by client ID, with
   * the text each came from, so the same entity keeps its ID until the text
   * changes.
   */
  private readonly _generatedIds = new Map<
    string,
    { text: string; id: string }
  >()

  /**
   * What runs once the parts being hydrated are all hydrated; `null` while
   * none are.
   */
  private _hydrationCallbacks: (() => void)[] | null = null

  @observable.ref accessor engineSymbols: EngineSymbols | null = null
  @observable.ref accessor organizerBindings: OrganizerBindings | null = null

  /**
   * @param _getBuilders The builders, in symbol order.
   */
  constructor(private readonly _getBuilders: () => EntityProvider[]) {}

  @computed
  get allEntityDescriptors(): EntityDescriptor[] {
    return this._getBuilders().flatMap((b) => b.entityDescriptors ?? [])
  }

  /** @inheritDoc */
  @computed
  get allEntities(): Entity[] {
    return this.allEntityDescriptors.map((x) => x.entity)
  }

  /** @inheritDoc */
  getAvailableEntityDescriptorsFor(
    target: SymbolContext | null,
  ): EntityDescriptor[] {
    if (!target) {
      return []
    }

    const builders = this._getBuilders()
    const builderIndex = builders.findIndex((b) => b === target)
    if (builderIndex > -1) {
      return builders
        .slice(0, builderIndex)
        .flatMap((b) => b.entityDescriptors ?? [])
    }

    const descriptors = this.allEntityDescriptors
    const entityIndex = descriptors.findIndex((d) => d.entity === target)
    return entityIndex > -1 ? descriptors.slice(0, entityIndex) : []
  }

  /** @inheritDoc */
  getSymbolTableFor(target: SymbolContext | null): SymbolTable {
    const table = new SymbolTable('root')
    if (this.engineSymbols) {
      table.setIdentifierSymbolsFromObject(this.engineSymbols)
    }
    for (const d of this.getAvailableEntityDescriptorsFor(target)) {
      if (d.entity.valueType) {
        table.setIdentifierSymbol(
          new RynoSymbol(
            d.entity.id,
            d.entity.valueType,
            SymbolKind.Identifier,
            true,
          ),
        )
      }
    }
    return table
  }

  /** @inheritDoc */
  validateValueType(type: RynoType, value: unknown): string[] | null {
    const { errors } = unify(
      getRynoTypeFromJsType(value),
      type,
      new SymbolTable(),
      Variance.Covariant,
    )
    return errors ?? null
  }

  /** @inheritDoc */
  newId(cid: string, text: string): string | undefined {
    if (!text) {
      return undefined
    }
    const previous = this._generatedIds.get(cid)
    if (previous?.text === text) {
      return previous.id
    }

    const taken = new Set(
      [
        ...this.allEntities.map((x) => x.id),
        ...[...this._generatedIds]
          .filter(([otherCid]) => otherCid !== cid)
          .map(([, generated]) => generated.id),
      ].filter(Boolean),
    )
    const base = generateIdFromText(
      cid.split('-')[0] || 'OBJ',
      text.substring(0, 30),
    )
    let id = base
    let count = 1
    while (taken.has(id)) {
      id = `${base}_${count++}`
    }
    this._generatedIds.set(cid, { text, id })
    return id
  }

  /**
   * Forgets the generated IDs, for a version whose entities are all new.
   */
  clearGeneratedIds() {
    this._generatedIds.clear()
  }

  /** @inheritDoc */
  afterHydrate(callback: () => void) {
    if (this._hydrationCallbacks) {
      this._hydrationCallbacks.push(callback)
    } else {
      callback()
    }
  }

  /**
   * Holds what {@link afterHydrate} is given until {@link endHydration}.
   */
  beginHydration() {
    this._hydrationCallbacks = []
  }

  /**
   * @returns What the parts asked to run once they are all hydrated.
   */
  endHydration() {
    const callbacks = this._hydrationCallbacks ?? []
    this._hydrationCallbacks = null
    return callbacks
  }
}
